import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Mail, ShieldCheck, Send, Sparkles } from "lucide-react";
import { WelcomeInviteButton } from "@/components/WelcomeInvite";

const genPass = () => "Mk" + Math.random().toString(36).slice(2, 8) + Math.floor(10 + Math.random() * 89) + "!";

// Azioni di gestione accesso utente. mode="admin" = dialog completo; mode="manager" = solo invio reset.
export function UserAccessActions({ user, mode = "admin", onChanged }) {
  const [open, setOpen] = useState(false);
  const protectedAccount = user.role === "admin" || user.email?.toLowerCase() === "andrea.perocchi@gmail.com";
  const [email, setEmail] = useState(user.email || "");
  const [pwd, setPwd] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [sendCreds, setSendCreds] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendReset = async () => {
    if (mode === "manager" && !window.confirm(`Inviare l'email di reset password a ${user.email}?`)) return;
    setBusy(true);
    try { await api.post(`/users/${user.id}/send-password-reset`); toast.success(`Email di reset inviata a ${user.email}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio"); }
    finally { setBusy(false); }
  };

  if (mode === "manager") {
    return (
      <>
        <WelcomeInviteButton user={user} compact />
        <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" disabled={busy} onClick={sendReset} title="Invia reset password" aria-label="Invia reset password" data-testid={`send-reset-${user.id}`}>
          <Send className="w-4 h-4" />
        </Button>
      </>
    );
  }

  const changeEmail = async () => {
    const e2 = email.trim().toLowerCase();
    if (!e2 || e2 === (user.email || "").toLowerCase()) { toast.error("Inserisci una nuova email diversa"); return; }
    setBusy(true);
    try { await api.put(`/admin/users/${user.id}/email`, { email: e2 }); toast.success("Email di accesso aggiornata"); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };
  const setPassword = async () => {
    if ((pwd || "").length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/set-password`, { password: pwd, force_change: forceChange, send_credentials: sendCreds });
      toast.success(sendCreds ? "Password impostata e credenziali inviate" : "Password impostata");
      setPwd("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };
  const resendVerification = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/send-verification`); toast.success("Email di verifica inviata"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1 shrink-0" data-testid={`access-actions-${user.id}`}><KeyRound className="w-4 h-4" /> Accesso</Button>
      </DialogTrigger>
      <DialogContent data-testid="user-access-dialog">
        <DialogHeader><DialogTitle>Gestione accesso — {user.name || user.email}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            {protectedAccount && <p className="text-sm text-muted-foreground mb-3" data-testid="access-protected-notice">Le credenziali di questo amministratore sono protette. Il titolare può usare la gestione personale o il recupero password.</p>}
            <Label className="flex items-center gap-1.5"><Mail className="w-4 h-4" /> Email di accesso</Label>
            <div className="flex gap-2">
              <Input value={email} disabled={busy || protectedAccount} onChange={(e) => setEmail(e.target.value)} data-testid="access-email-input" />
              <Button next variant="secondary" disabled={busy || protectedAccount} onClick={changeEmail} data-testid="access-email-save">Salva</Button>
            </div>
          </div>
          <div className="space-y-3 border-t border-border pt-4">
            <Label className="flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Imposta nuova password</Label>
            <div className="flex gap-2">
              <Input value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Nuova password" data-testid="access-password-input" />
              <Button type="button" variant="outline" className="gap-1" onClick={() => setPwd(genPass())} data-testid="access-password-generate"><Sparkles className="w-4 h-4" /> Genera</Button>
            </div>
            <div className="flex items-center justify-between"><span className="text-sm">Forza cambio password al primo accesso</span><Switch checked={forceChange} onCheckedChange={setForceChange} data-testid="access-force-change" /></div>
            <div className="flex items-center justify-between"><span className="text-sm">Invia le credenziali via email all'utente</span><Switch checked={sendCreds} onCheckedChange={setSendCreds} data-testid="access-send-creds" /></div>
            <Button className="w-full" disabled={busy || protectedAccount} onClick={setPassword} data-testid="access-password-save">Imposta password</Button>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            <Button variant="outline" className="gap-1" disabled={busy} onClick={sendReset} data-testid="access-send-reset"><Send className="w-4 h-4" /> Invia email reset password</Button>
            {user.role === "dealer" && <WelcomeInviteButton user={user} testid="access-send-welcome" />}
            <Button variant="outline" className="gap-1" disabled={busy} onClick={resendVerification} data-testid="access-send-verification"><ShieldCheck className="w-4 h-4" /> Reinvia verifica email</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
